import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * M11 L11.01 — Closure Queue page.
 *
 * Lists BO-final verifications by status. Reads tiles + queue from
 * `/v1/closure/dashboard/*`. Row click navigates to L11.02 by
 * `verificationId`.
 */
interface ClosureTiles {
  readonly inReview: number;
  readonly inDecision: number;
  readonly rework: number;
  readonly evidenceRequested: number;
  readonly overdue: number;
  readonly closedToday: number;
  readonly approved: number;
  readonly rejected: number;
  readonly escalated: number;
  readonly highPriority: number;
  readonly slaViolations: number;
}

interface ClosureQueueItem {
  readonly verificationId: string;
  readonly jobNumber: string | null;
  readonly status: string;
  readonly cycleNo: number;
  readonly slaDeadlineAt: string;
  readonly startedAt: string | null;
  readonly overallQualityScore: number | null;
  readonly brandComplianceScore: number | null;
}

interface QueueResponse {
  readonly items: readonly ClosureQueueItem[];
  readonly total: number;
}

const STATUS_OPTIONS = [
  '',
  'IN_REVIEW',
  'IN_DECISION',
  'APPROVED',
  'REJECTED',
  'REWORK',
  'EVIDENCE_REQUESTED',
  'CLOSED',
] as const;

export function ClosureQueuePage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [tiles, setTiles] = useState<ClosureTiles | null>(null);
  const [page, setPage] = useState<QueueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('');
  const [offset, setOffset] = useState<number>(0);
  const limit = 25;

  useEffect(() => {
    let alive = true;
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    qs.set('offset', String(offset));
    qs.set('limit', String(limit));
    Promise.all([
      apiRequest<ClosureTiles>('/v1/closure/dashboard/tiles', { token }),
      apiRequest<QueueResponse>(`/v1/closure/dashboard/queue?${qs.toString()}`, { token }),
    ])
      .then(([t, q]) => {
        if (!alive) return;
        setTiles(t);
        setPage(q);
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [token, status, offset]);

  if (error) {
    return (
      <main aria-label="closure-queue">
        <h1>{t('closure.queue.h1')}</h1>
        <p role="alert">{error}</p>
      </main>
    );
  }
  if (!tiles || !page) {
    return (
      <main aria-label="closure-queue">
        <p>{t('closure.queue.loading')}</p>
      </main>
    );
  }
  const totalPages = Math.max(1, Math.ceil(page.total / limit));
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <main aria-label="closure-queue">
      <h1>{t('closure.queue.title')}</h1>

      <ul aria-label="closure-tiles">
        <li aria-label="tile-in-review">{t('closure.queue.tile.inReview')}: {tiles.inReview}</li>
        <li aria-label="tile-in-decision">{t('closure.queue.tile.inDecision')}: {tiles.inDecision}</li>
        <li aria-label="tile-rework">{t('closure.queue.tile.rework')}: {tiles.rework}</li>
        <li aria-label="tile-evidence">{t('closure.queue.tile.evidence')}: {tiles.evidenceRequested}</li>
        <li aria-label="tile-overdue">{t('closure.queue.tile.overdue')}: {tiles.overdue}</li>
        <li aria-label="tile-closed-today">{t('closure.queue.tile.closedToday')}: {tiles.closedToday}</li>
        <li aria-label="tile-approved">{t('closure.queue.tile.approved')}: {tiles.approved}</li>
        <li aria-label="tile-rejected">{t('closure.queue.tile.rejected')}: {tiles.rejected}</li>
        <li aria-label="tile-escalated">{t('closure.queue.tile.escalated')}: {tiles.escalated}</li>
        <li aria-label="tile-high-priority">{t('closure.queue.tile.highPriority')}: {tiles.highPriority}</li>
        <li aria-label="tile-sla-violations">{t('closure.queue.tile.slaViolations')}: {tiles.slaViolations}</li>
      </ul>

      <section aria-label="queue-filters">
        <label htmlFor="status-filter">{t('closure.queue.status')}</label>
        <select
          id="status-filter"
          aria-label="status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setOffset(0);
          }}
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s || 'all'} value={s}>
              {s || t('closure.queue.all')}
            </option>
          ))}
        </select>
      </section>

      {page.items.length === 0 ? (
        <p aria-label="empty">{t('closure.queue.empty')}</p>
      ) : (
        <table aria-label="queue-table">
          <thead>
            <tr>
              <th>{t('closure.queue.col.job')}</th>
              <th>{t('closure.queue.col.status')}</th>
              <th>{t('closure.queue.col.cycle')}</th>
              <th>{t('closure.queue.col.slaDue')}</th>
              <th>{t('closure.queue.col.started')}</th>
              <th>{t('closure.queue.col.quality')}</th>
              <th>{t('closure.queue.col.brand')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {page.items.map((row) => (
              <tr key={row.verificationId} aria-label={`row-${row.verificationId}`}>
                <td>{row.jobNumber ?? '—'}</td>
                <td>
                  <span aria-label={`status-${row.verificationId}`}>{row.status}</span>
                </td>
                <td>{row.cycleNo}</td>
                <td>{at(row.slaDeadlineAt)}</td>
                <td>{at(row.startedAt)}</td>
                <td>{decimal(row.overallQualityScore)}</td>
                <td>{decimal(row.brandComplianceScore)}</td>
                <td>
                  <Link
                    to={`/closure/final/${row.verificationId}`}
                    aria-label={`open-${row.verificationId}`}
                  >
                    {t('closure.queue.open')}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <nav aria-label="queue-pagination">
        <button
          type="button"
          aria-label="prev-page"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - limit))}
        >
          {t('closure.queue.prev')}
        </button>
        <span aria-label="page-info">
          {t('closure.queue.pageInfo', { current: currentPage, total: totalPages })}
        </span>
        <button
          type="button"
          aria-label="next-page"
          disabled={currentPage >= totalPages}
          onClick={() => setOffset(offset + limit)}
        >
          {t('closure.queue.next')}
        </button>
      </nav>
    </main>
  );
}
